import React, { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { naoPerturbeAPI, formatarErroAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { PhoneOff, Loader2, CheckCircle2, AlertTriangle, KeyRound, ChevronDown, Search, RotateCcw, Trash2, ShieldCheck, ExternalLink } from 'lucide-react';
import { NaoPerturbeBadge } from '@/components/NaoPerturbe';
import { NaoPerturbeConector, PORTAL_LNP_URL } from '@/components/NaoPerturbeConector';


const fmtDataHora = (iso) => (iso ? new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-');
const fmtDuracao = (min) => {
  if (min == null) return '?';
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return h >= 48 ? `${Math.floor(h / 24)} dias` : `${h}h${m ? ` ${m}min` : ''}`;
};

const Passos = () => {
  const [aberto, setAberto] = useState(true);
  return (
    <div className="rounded-lg border border-dashed p-3 text-sm" data-testid="lnp-passos">
      <button onClick={() => setAberto(!aberto)} className="w-full flex items-center justify-between font-medium text-left" data-testid="lnp-passos-toggle">
        <span>Como copiar o token da Conexão Claro (≈1 min)</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${aberto ? 'rotate-180' : ''}`} />
      </button>
      {aberto && (
        <ol className="list-decimal ml-5 mt-3 space-y-1.5 text-gray-600 dark:text-gray-300">
          <li>Entre no portal <b>app.conexaoclarobrasil.com.br</b> com seu usuário e abra <b>Lei Não Perturbe → Consulta de Números</b>.</li>
          <li>Pressione <b>F12</b> (Ferramentas do desenvolvedor) e abra a aba <b>Network / Rede</b>.</li>
          <li>Consulte qualquer telefone no portal. Clique na linha <b>lei-nao-perturbe</b> que aparecer na lista.</li>
          <li>Em <b>Headers / Cabeçalhos → Request Headers</b>, localize <b>Authorization: Bearer …</b> e copie o valor após "Bearer" (pode colar com ou sem a palavra "Bearer").</li>
          <li>Cole abaixo e clique em <b>Salvar e validar</b>. O CRM testa o token na hora e mostra até quando ele vale.</li>
        </ol>
      )}
    </div>
  );
};

const NaoPerturbe = () => {
  const { user } = useAuth();
  const isAdmin = user?.perfil === 'ADMIN';
  const [cfg, setCfg] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [token, setToken] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [numeros, setNumeros] = useState('');
  const [consultando, setConsultando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [reprocessando, setReprocessando] = useState(false);

  const carregar = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const res = await naoPerturbeAPI.config();
      setCfg(res.data);
    } catch (e) {
      toast.error(formatarErroAPI(e));
    } finally {
      setCarregando(false);
    }
  }, [isAdmin]);

  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => {
    const t = setInterval(carregar, 30000);
    return () => clearInterval(t);
  }, [carregar]);

  const salvar = async () => {
    if (!token.trim()) { toast.error('Cole o token primeiro'); return; }
    setSalvando(true);
    try {
      const res = await naoPerturbeAPI.salvarConfig({ token });
      toast.success(res.data.message);
      setToken('');
      setCfg(res.data);
    } catch (e) {
      toast.error(formatarErroAPI(e));
    } finally {
      setSalvando(false);
    }
  };

  const remover = async () => {
    if (!window.confirm('Remover o token? As consultas automáticas ficarão desativadas.')) return;
    try {
      await naoPerturbeAPI.removerConfig();
      toast.success('Token removido');
      carregar();
    } catch (e) {
      toast.error(formatarErroAPI(e));
    }
  };

  const consultar = async () => {
    const lista = numeros.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean);
    if (!lista.length) { toast.error('Informe ao menos um telefone'); return; }
    setConsultando(true);
    try {
      const res = await naoPerturbeAPI.consultar(lista);
      setResultado(res.data);
      if (res.data.status !== 'ok') toast.error(res.data.mensagem);
    } catch (e) {
      toast.error(formatarErroAPI(e));
    } finally {
      setConsultando(false);
    }
  };

  const reprocessar = async () => {
    setReprocessando(true);
    try {
      const res = await naoPerturbeAPI.reprocessar(30);
      toast.success(res.data.message);
      carregar();
    } catch (e) {
      toast.error(formatarErroAPI(e));
    } finally {
      setReprocessando(false);
    }
  };

  const StatusBadge = () => {
    if (cfg?.conector?.ativo && cfg.conector.sessao === 'ok') return <Badge className="gap-1 bg-emerald-600 hover:bg-emerald-600" data-testid="lnp-status-badge"><CheckCircle2 className="w-3 h-3" /> Ativo{cfg.valido ? '' : ' (via conector)'}</Badge>;
    if (!cfg?.configurado) return <Badge variant="outline" className="gap-1 text-gray-500" data-testid="lnp-status-badge"><AlertTriangle className="w-3 h-3" /> Não configurado</Badge>;
    if (!cfg.valido) return <Badge className="gap-1 bg-red-600 hover:bg-red-600" data-testid="lnp-status-badge"><AlertTriangle className="w-3 h-3" /> {cfg.vencido ? 'Token expirado' : 'Token inválido'}</Badge>;
    return <Badge className="gap-1 bg-emerald-600 hover:bg-emerald-600" data-testid="lnp-status-badge"><CheckCircle2 className="w-3 h-3" /> Ativo</Badge>;
  };

  if (!isAdmin) return <Navigate to="/dashboard" replace />;
  if (carregando) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-purple-600" /></div>;

  return (
    <div className="space-y-6 max-w-5xl" data-testid="nao-perturbe-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><PhoneOff className="w-6 h-6 text-red-600" /> Lei Não Perturbe</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">Consulta automática dos telefones da venda na Conexão Claro — o selo <b>BQ</b> (bloqueado) ou <b>LB</b> (liberado) aparece ao lado de cada telefone no Detalhar.</p>
        </div>
        <div className="flex items-center gap-2">
          <a href={PORTAL_LNP_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-semibold hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors" data-testid="lnp-abrir-portal-link">
            <ExternalLink className="w-3.5 h-3.5" /> Portal Conexão Claro
          </a>
          <StatusBadge />
        </div>
      </div>

      {isAdmin && <NaoPerturbeConector conector={cfg?.conector} onAtualizar={carregar} />}


      {cfg?.servidor_sem_conexao && (
        <div className="rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 text-sm text-amber-800 dark:text-amber-200" data-testid="lnp-aviso-sem-conexao">
          O servidor do CRM está sem conexão com a Conexão Claro no momento (bloqueio de IP). {cfg?.conector?.ativo ? 'As consultas estão sendo feitas pelo conector do navegador.' : 'Instale o conector no navegador do admin para as consultas continuarem.'}
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-5">
        <Card className="lg:col-span-3">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2"><KeyRound className="w-4 h-4" /> Token da Conexão Claro</CardTitle>
            <CardDescription>O token é a sessão de um usuário logado no portal. O CRM mantém a sessão viva com uma verificação a cada 5 minutos; se o portal encerrar a sessão (logout, novo login em outro lugar ou limite da Claro), o CRM avisa e basta colar um novo — as vendas que ficaram pendentes são consultadas automaticamente.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {cfg?.configurado && (
              <div className="rounded-lg border bg-gray-50 dark:bg-gray-800/50 p-3 text-sm grid grid-cols-2 gap-x-4 gap-y-1.5" data-testid="lnp-config-info">
                <span className="text-gray-500">Token</span><span className="font-mono text-xs truncate">{cfg.token_mascarado}</span>
                <span className="text-gray-500">Usuário do portal</span><span className="truncate">{cfg.usuario_token || '-'}</span>
                <span className="text-gray-500">Válido até</span><span className={cfg.vencido ? 'text-red-600 font-semibold' : ''} data-testid="lnp-expira-em">{cfg.expira_em ? fmtDataHora(cfg.expira_em) : 'enquanto a sessão do portal estiver ativa'}</span>
                <span className="text-gray-500">Atualizado</span><span>{fmtDataHora(cfg.atualizado_em)}{cfg.atualizado_por ? ` • ${cfg.atualizado_por}` : ''}</span>
                <span className="text-gray-500">Consultas</span><span>{cfg.consultas || 0}{cfg.ultima_consulta_em ? ` • última ${fmtDataHora(cfg.ultima_consulta_em)}` : ''}</span>
                <span className="text-gray-500">Sessão mantida viva</span><span>{cfg.keepalive_em ? `${fmtDataHora(cfg.keepalive_em)} (verificação a cada 5 min)` : '-'}</span>
                {cfg.pendentes > 0 && <><span className="text-gray-500">Vendas pendentes</span><span className="text-amber-600 font-semibold" data-testid="lnp-pendentes">{cfg.pendentes} (últimos 30 dias)</span></>}
                {cfg.erro && <><span className="text-gray-500">Erro</span><span className="text-red-600">{cfg.erro}{cfg.erro_em ? ` • ${fmtDataHora(cfg.erro_em)}` : ''}</span></>}
              </div>
            )}
            {cfg?.historico_tokens?.length > 0 && (
              <div className="rounded-lg border p-3" data-testid="lnp-historico-tokens">
                <p className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-2">Quanto durou cada token</p>
                <div className="space-y-1">
                  {cfg.historico_tokens.map((h, i) => (
                    <div key={i} className="flex flex-wrap items-center justify-between gap-2 text-xs">
                      <span className="font-mono text-gray-500">{h.token_mascarado}</span>
                      <span className="truncate max-w-[180px]">{h.usuario_token || '-'}</span>
                      <span className="text-gray-500">{fmtDataHora(h.atualizado_em)} → {fmtDataHora(h.invalidado_em)}</span>
                      <span className={`font-semibold ${h.duracao_min != null && h.duracao_min < 60 ? 'text-red-600' : 'text-emerald-600'}`}>{fmtDuracao(h.duracao_min)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <Passos />
            <div>
              <Label className="text-xs">Cole o token (valor após "Bearer")</Label>
              <Textarea value={token} onChange={(e) => setToken(e.target.value)} rows={3} placeholder="Bearer f74d0025-a546-4bfc-8420-16ccf02a1554" className="font-mono text-xs mt-1" data-testid="lnp-token-input" />
            </div>
            <div className="flex flex-wrap gap-2">
              <Button onClick={salvar} disabled={salvando} className="bg-purple-600 hover:bg-purple-700" data-testid="lnp-salvar-button">
                {salvando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />} Salvar e validar
              </Button>
              {cfg?.configurado && cfg.valido && (
                <Button variant="outline" onClick={reprocessar} disabled={reprocessando} data-testid="lnp-reprocessar-button" title="Consulta as vendas dos últimos 30 dias que ainda não têm resultado">
                  {reprocessando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-2" />} Consultar vendas pendentes (30 dias)
                </Button>
              )}
              {cfg?.configurado && isAdmin && (
                <Button variant="ghost" onClick={remover} className="text-red-600 hover:text-red-700" data-testid="lnp-remover-button"><Trash2 className="w-4 h-4 mr-2" /> Remover</Button>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2"><Search className="w-4 h-4" /> Consulta de números</CardTitle>
            <CardDescription>Digite um ou mais telefones (separados por vírgula ou linha).</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Textarea value={numeros} onChange={(e) => setNumeros(e.target.value)} rows={3} placeholder="21983946499, 11999998888" data-testid="lnp-numeros-input" />
            <Button onClick={consultar} disabled={consultando || (!cfg?.valido && !cfg?.conector?.ativo)} className="w-full" data-testid="lnp-consultar-button">
              {consultando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />} Consultar
            </Button>
            {resultado && (
              <div className="divide-y rounded-lg border" data-testid="lnp-resultado">
                {resultado.telefones.map((t, i) => (
                  <div key={i} className="flex items-center justify-between gap-2 px-3 py-2 text-sm">
                    <span className="font-mono">{t.original}</span>
                    {t.valido ? <NaoPerturbeBadge info={t.codigo ? t : null} size="md" testid={`lnp-resultado-${i}`} /> : <span className="text-xs text-red-500">Número inválido</span>}
                  </div>
                ))}
                <p className="px-3 py-1.5 text-[11px] text-gray-500">Consultado em {fmtDataHora(resultado.consultado_em)}{resultado.via === 'conector' ? ' • via conector do navegador' : ''}</p>
              </div>
            )}
            <div className="flex gap-3 text-[11px] text-gray-500 pt-1">
              <span className="inline-flex items-center gap-1"><span className="rounded bg-emerald-600 px-1.5 py-0.5 text-white font-bold">LB</span> Liberado para ligação</span>
              <span className="inline-flex items-center gap-1"><span className="rounded bg-red-600 px-1.5 py-0.5 text-white font-bold">BQ</span> Bloqueado para ligação</span>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default NaoPerturbe;
